import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ManageCollectionsService } from '../services/manage-collections.service';
import { NotificationService } from '../services/notification.service';
import { RoleViewService } from '../services/role-view.service';
import { TutorialVideoStep, TutorialVideoWidgetComponent } from '../components/tutorial-video-widget/tutorial-video-widget.component';
import { ADMIN_TUTORIAL_FLOW } from '../components/tutorial-video-widget/tutorial-video-flows';

@Component({
  selector: 'app-managecollections',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, TutorialVideoWidgetComponent],
  templateUrl: './managecollections.component.html',
  styleUrl: './managecollections.component.css'
})
export class ManagecollectionsComponent implements OnInit {
  readonly adminTutorialSequence: TutorialVideoStep[] = ADMIN_TUTORIAL_FLOW;

  private readonly SUB_COLLECTION_EXISTS_ERROR = 'Sub-collection exists';
  private readonly COLLECTION_EXISTS_ERROR = 'Collection exists';
  showAddModal = false;
  newCollectionName = '';
  collectionNameError = '';
  showEditModal = false;
  editCollectionName = '';
  editCollectionNameError = '';
  selectedCollectionForEdit: any = null;
  searchText = '';
  showDeleted = false;
  cardHeightPx = 0;
  scrHeight = '';
  isAddingCollection = false;     // For add operation
  isAddingSubCollection = false;  // For add sub-collection operation
  isEditingSubCollection = false; // For edit sub-collection operation
  isDeletingCollection = false;   // For delete operation
  userRole = 'superAdmin';
  workDayId = '';
  userName = '';
  // New properties for dual-tab modal
  selectedTabIndex = 0;
  newSubCollections: string[] = [''];
  subCollectionArray = [{ value: '' }];
  collectionExists = false;
  subCollectionExists: boolean[] = [];
  isFormValid = false;
  selectedCollectionForAddSub: any = null;
  dialogSearchTerm = '';
  filteredCollectionsDialog: any[] = [];
  datePipe: any;

  @HostListener('window:resize', ['$event'])
  getScreenSize(_event?: Event) {
    this.scrHeight = `${window.innerHeight - 100}px`;
  }

  constructor(
    private readonly manageCollectionsService: ManageCollectionsService,
    private readonly notificationService: NotificationService,public readonly roleviewservice: RoleViewService
  ) {
    this.getScreenSize();
  }

  ngOnInit(): void {
    this.getUserInfo();
    this.loadCollectionsFromAPI();
  }

  getUserInfo() {
    this.workDayId = this.roleviewservice.getWorkdayId() || '';
    this.userRole= this.roleviewservice.getActualRole();
    this.userName = this.roleviewservice.getUserName();
  }

  get stats() {
    return [
      { title: 'Active Collections', icon: 'folder_open', color: '#FFFFFF', accent: '#cc0000', number: this.getActiveCollectionsCount() },
      { title: 'Active Sub-Collections', icon: 'layers', color: '#FFFFFF', accent: '#1E88E5', number: this.getActiveSubCollectionsCount() },
      { title: 'Archived Collections', icon: 'delete_outline', color: '#FFFFFF', accent: '#6B7280', number: this.getDeletedCollectionsCount() },
      { title: 'Archived Sub-Collections', icon: 'archive', color: '#FFFFFF', accent: '#FB8C00', number: this.getDeletedSubCollectionsCount() },
    ];
  }

  // Deleted collections list shown when Deleted tab is active
  deletedCollections: Array<{
    id: number;
    name: string;
    updated: string;
    deletedAt: string;
    expanded?: boolean;
    deletedSubCollections?: Array<{ id: number; name: string; deletedAt: string }>;
  }> = [];

  // Collections will be loaded from API
  collections: any[] = [];

  toggleDeleted() {
    this.showDeleted = !this.showDeleted;
  }

  setSearch(val: string) {
    this.searchText = val;
  }

  toggleExpand(col: any) {
    col.expanded = !col.expanded;
  }

  editCollection(col: any) {
    console.info('Edit', col);
    this.selectedCollectionForEdit = col;
    this.editCollectionName = col.name;
    this.editCollectionNameError = '';
    this.showEditModal = true;
  }

  checkEditCollectionDuplicate() {
    const name = (this.editCollectionName || '').trim();
    if (!name) {
      this.editCollectionNameError = '';
      return;
    }
    
    const isDuplicate = this.collections.some(c => 
      c.id !== this.selectedCollectionForEdit.id && 
      c.name.toLowerCase() === name.toLowerCase()
    );
    if (isDuplicate) {
      this.editCollectionNameError = this.COLLECTION_EXISTS_ERROR;
    } else {
      this.editCollectionNameError = '';
    }
  }

  submitEditCollection() {
    const name = (this.editCollectionName || '').trim();
    if (!name || !this.selectedCollectionForEdit) {
      return;
    }
    
    // Check for duplicate collection name (excluding current)
    const isDuplicate = this.collections.some(c => 
      c.id !== this.selectedCollectionForEdit.id && 
      c.name.toLowerCase() === name.toLowerCase()
    );
    if (isDuplicate) {
      this.editCollectionNameError = this.COLLECTION_EXISTS_ERROR;
      return;
    }
    
    // Call API to update collection
    this.isAddingCollection = true;
    const request = {
      collectionId: this.selectedCollectionForEdit.id,
      collectionName: name,
      updatedBy: this.userName
    };

    this.manageCollectionsService.updateCollection(this.workDayId, request).subscribe({
      next: (response: any) => {
        this.isAddingCollection = false;
        if (response && (response.success !== false)) {
          this.notificationService.showSuccess('Collection updated successfully!');
          this.selectedCollectionForEdit.name = name;
          this.selectedCollectionForEdit.updated = new Date().toISOString();
          this.cancelEditCollection();
          this.loadCollectionsFromAPI(); // Reload the list
        } else {
          this.isAddingCollection = false;
          this.notificationService.showError('Failed to update collection');
        }
      },
      error: (_error: any) => {
        this.isAddingCollection = false;
        this.notificationService.showError('Failed to update collection');
      }
    });
  }

  cancelEditCollection() {
    this.showEditModal = false;
    this.editCollectionName = '';
    this.editCollectionNameError = '';
    this.selectedCollectionForEdit = null;
  }

  deleteCollection(col: any) {
    this.deleteConfirmTitle = col.name;
    this.deleteType = 'collection';
    this.deleteConfirmCallback = () => {
      console.info('Delete', col);
      
      // Call API to delete collection
      this.isDeletingCollection = true;
      this.manageCollectionsService.deleteCollection(
        col.id.toString(), 
        this.userName
      ).subscribe({
        next: (response: any) => {
          this.isDeletingCollection = false;
          // Only show success if response indicates success
          if (response && (response.success !== false)) {
            this.notificationService.showSuccess('Collection archived successfully!');
            
            console.info('Delete success, collection:', col);
            console.info('Before delete - collections count:', this.collections.length);
          
          // Remove from active collections
          this.collections = this.collections.filter(c => c.id !== col.id);
          
          console.info('After delete - collections count:', this.collections.length);
          
          // Add to deleted collections with its deleted sub-collections
          const deletedItem = {
            id: col.id,
            name: col.name,
            updated: col.updated,
            deletedAt: col.updatedAt ? new Date(col.updatedAt).toISOString() : col.updated,
            expanded: false,
            deletedSubCollections: col.deletedSubCollections || []
          };
          
          this.deletedCollections.unshift(deletedItem);
          
          console.info('After add to deletedCollections - deleted count:', this.deletedCollections.length);
          console.info('Deleted collections array:', this.deletedCollections);
          console.info('Setting showDeleted to true');
          
          // Automatically switch to deleted collections view
          this.showDeleted = true;
          
          console.info('Final state - showDeleted:', this.showDeleted);
          
          // Reload data to get latest updated_at timestamp from API
          this.loadCollectionsFromAPI();
          } else {
            this.notificationService.showError('Failed to archive collection');
          }
        },
        error: (error: any) => {
          this.isDeletingCollection = false;
          console.trace('Error archiving collection:', error);
          this.notificationService.showError('Error archiving collection');
        }
      });
    };
    this.showDeleteConfirmModal = true;
  }

  confirmDelete() {
    if (this.deleteConfirmCallback) {
      this.deleteConfirmCallback();
    }
    this.cancelDelete();
  }

  cancelDelete() {
    this.showDeleteConfirmModal = false;
    this.deleteConfirmTitle = '';
    this.deleteConfirmCallback = null;
  }

  openAddDialog() {
    this.selectedTabIndex = 0;
    this.newCollectionName = '';
    this.isFormValid = false;
    this.newSubCollections = [''];
    this.subCollectionArray = [{ value: '' }];
    this.collectionExists = false;
    this.subCollectionExists = [];
    this.selectedCollectionForAddSub = null;
    this.showAddModal = true;
  }

  validateCollectionName() {
    const newName = this.newCollectionName?.trim().toLowerCase();
    if (!newName) {
      this.collectionExists = false;
      return;
    }
    // check duplicates
    this.collectionExists = this.collections.some(
      (c: any) => c.name?.trim().toLowerCase() === newName
    );
  }

  validateSubCollectionName(index: number) {
    const newName = this.newSubCollections[index]?.trim().toLowerCase();
    if (!newName) {
      this.subCollectionExists[index] = false;
      return;
    }

    if (this.selectedTabIndex === 1 && this.selectedCollectionForAddSub) {
      // For Add Sub-Collection tab, check against selected collection's sub-collections
      const existingNames = this.selectedCollectionForAddSub?.subCollections?.map(
        (sc: any) => sc.name.trim().toLowerCase()
      ) || [];
      this.subCollectionExists[index] = existingNames.includes(newName);
    } else {
      // For Add Collection tab, validation can be minimal
      this.subCollectionExists[index] = false;
    }
  }

  validateForm() {
    if (this.selectedTabIndex === 0) {
      // Add Collection tab
      const hasCollectionName = this.newCollectionName?.trim().length > 0;
      const noErrors = !this.collectionExists && this.subCollectionExists.every(err => !err);
      this.isFormValid = hasCollectionName && noErrors;
    } else {
      // Add Sub-Collection tab
      const hasSelectedCollection = !!this.selectedCollectionForAddSub?.id;
      const hasValidSubCollections = this.newSubCollections.some(
        (s: string) => (s || '').trim().length > 0
      );
      const noErrors = this.subCollectionExists.every(err => !err);
      this.isFormValid = hasSelectedCollection && hasValidSubCollections && noErrors;
    }
  }

  onTabChange(index: number) {
    this.selectedTabIndex = index;

    if (index === 0) {
      // Reset Add Collection tab
      this.newCollectionName = '';
      this.subCollectionArray = [{ value: '' }];
      this.collectionExists = false;
      this.subCollectionExists = [];
    } else {
      // Reset Add Sub Collection tab
      this.newSubCollections = [''];
      this.subCollectionExists = [];
      this.selectedCollectionForAddSub = null;
    }
    this.isFormValid = false;
  }

  trackByIndex(index: number): number {
    return index;
  }

  addSubCollectionField() {
    this.subCollectionArray.push({ value: '' });
  }

  removeSubCollectionField(index: number) {
    this.subCollectionArray.splice(index, 1);
  }

  removeNewSubCollectionField(index: number) {
    this.newSubCollections.splice(index, 1);
  }

  addNewSubCollectionField() {
    this.newSubCollections.push('');
  }

  selectCollectionForAddSub(collection: any) {
    this.selectedCollectionForAddSub = collection;
    this.dialogSearchTerm = '';
    this.filteredCollectionsDialog = [];
  }

  filterCollectionsDialog() {
    const searchTerm = (this.dialogSearchTerm || '').trim().toLowerCase();
    if (searchTerm === '') {
      this.filteredCollectionsDialog = [];
    } else {
      this.filteredCollectionsDialog = this.collections.filter((col: any) =>
        (col.name || '').toLowerCase().includes(searchTerm)
      );
    }
  }

  saveSubCollection(tabIndex: number) {
    const name = (this.newCollectionName || '').trim();

    // Tab 0 (Add Collection) sub inputs come from subCollectionArray[i].value
    const subsTab0 = (this.subCollectionArray || [])
      .map((s: any) => (s?.value || '').trim())
      .filter((s: string) => !!s);

    // Tab 1 (Add Sub Collection) inputs come from newSubCollections[i]
    const subsTab1 = (this.newSubCollections || [])
      .map((s: string) => (s || '').trim())
      .filter((s: string) => !!s);

    if (tabIndex === 0) {
      // Add Collection - with or without sub-collections
      if (!name) {
        this.notificationService.showError('Collection name is required');
        return;
      }
      if (subsTab0.length > 0) {
        this.saveCollectionWithSubCollections(name, subsTab0);
      } else {
        this.saveSingleCollection(name);
      }
    } else if (tabIndex === 1) {
      // Add Sub-Collection to existing collection
      if (!this.selectedCollectionForAddSub?.id) {
        this.notificationService.showError('Select a collection');
        return;
      }
      if (!subsTab1.length) {
        this.notificationService.showError('Add at least one sub-collection');
        return;
      }
      this.saveSubCollectionsToExisting(this.selectedCollectionForAddSub.id, subsTab1);
    }
  }

  saveSingleCollection(collectionName: string) {
    this.isAddingCollection = true;
    const request = {
      collectionName: collectionName.trim(),
      createdBy: this.userName
    };

    this.manageCollectionsService.saveCollections(request).subscribe({
      next: (response: any) => {
        this.isAddingCollection = false;
        if (response && (response.success !== false)) {
          this.notificationService.showSuccess('Collection created successfully!');
          this.resetAddForm();
          this.showAddModal = false;
          this.loadCollectionsFromAPI();
        } else {
          this.notificationService.showError('Failed to create collection');
        }
      },
      error: (error: any) => {
        this.isAddingCollection = false;
        console.trace('Error creating collection:', error);
        this.notificationService.showError('Error creating collection');
      }
    });
  }

  saveCollectionWithSubCollections(collectionName: string, subCollections: string[]) {
    this.isAddingCollection = true;
    const request = {
      collectionName: collectionName.trim(),
      subCollections: subCollections,
      createdBy: this.userName
    };

    this.manageCollectionsService.saveCollectionWithSubCollections(this.workDayId, request).subscribe({
      next: (response: any) => {
        this.isAddingCollection = false;
        if (response && (response.success !== false)) {
          this.notificationService.showSuccess('Collection with sub-collections created successfully!');
          this.resetAddForm();
          this.showAddModal = false;
          this.loadCollectionsFromAPI();
        } else {
          this.notificationService.showError('Failed to create collection with sub-collections');
        }
      },
      error: (error: any) => {
        this.isAddingCollection = false;
        console.trace('Error creating collection with sub-collections:', error);
        this.notificationService.showError('Error creating collection with sub-collections');
      }
    });
  }

  saveSubCollectionsToExisting(collectionId: number, subCollectionNames: string[]) {
    this.isAddingSubCollection = true;
    
    // Send sub-collections one by one or as array depending on API
    const request = {
      collectionId: collectionId,
      subCollectionName: subCollectionNames, // API might accept array here
      createdBy: this.userName
    };

    this.manageCollectionsService.saveSubCollections(this.workDayId, request).subscribe({
      next: (response: any) => {
        this.isAddingSubCollection = false;
        if (response && (response.success !== false)) {
          this.notificationService.showSuccess('Sub-collections added successfully!');
          this.resetAddForm();
          this.showAddModal = false;
          this.loadCollectionsFromAPI();
        } else {
          this.notificationService.showError('Failed to add sub-collections');
        }
      },
      error: (error: any) => {
        this.isAddingSubCollection = false;
        console.trace('Error adding sub-collections:', error);
        this.notificationService.showError('Error adding sub-collections');
      }
    });
  }

  resetAddForm() {
    this.newCollectionName = '';
    this.newSubCollections = [''];
    this.subCollectionArray = [{ value: '' }];
    this.selectedCollectionForAddSub = null;
    this.isFormValid = false;
    this.collectionExists = false;
    this.subCollectionExists = [];
    this.dialogSearchTerm = '';
    this.filteredCollectionsDialog = [];
  }

  cancelAddCollection() {
    this.showAddModal = false;
    this.resetAddForm();
  }

  addSubCollection(col: any) {
    // open sub-collection modal for the given collection
    this.selectedCollectionForSub = col;
    this.newSubName = '';
    this.subCollectionNameError = '';
    this.showAddSubModal = true;
  }

  showAddSubModal = false;
  newSubName = '';
  subCollectionNameError = '';
  selectedCollectionForSub: any = null;
  showEditSubModal = false;
  editSubName = '';
  editSubNameError = '';
  selectedSubForEdit: any = null;
  selectedCollectionForEditSub: any = null;
  showDeleteConfirmModal = false;
  deleteConfirmTitle = '';
  deleteConfirmCallback: (() => void) | null = null;
  deleteType: 'collection' | 'sub-collection' = 'collection';

  checkAddSubCollectionDuplicate() {
    const name = (this.newSubName || '').trim();
    if (!name || !this.selectedCollectionForSub) {
      this.subCollectionNameError = '';
      return;
    }
    
    const parent = this.selectedCollectionForSub;
    const isDuplicate = parent.subCollections?.some(
  (s: any) => s.name.toLowerCase() === name.toLowerCase()
   ) ?? false;
    if (isDuplicate) {
      this.subCollectionNameError = this.SUB_COLLECTION_EXISTS_ERROR;
    } else {
      this.subCollectionNameError = '';
    }
  }

  submitAddSubCollection() {
    const name = (this.newSubName || '').trim();
    if (!name || !this.selectedCollectionForSub) {
      return;
    }
    
    const parent = this.selectedCollectionForSub;
    // Check for duplicate sub-collection name within the parent
    const isDuplicate = parent.subCollections?.some(
      (s: any) => s.name.toLowerCase() === name.toLowerCase()
    ) ?? false;
    if (isDuplicate) {
      this.subCollectionNameError = this.SUB_COLLECTION_EXISTS_ERROR;
      return;
    }
    
    // Call API to save sub-collection
    this.isAddingSubCollection = true;
    const request = {
      collectionId: parent.id,
      createdBy: this.userName,
      subCollectionName: [name]
    };

    this.manageCollectionsService.saveSubCollections(this.workDayId, request).subscribe({
      next: (response: any) => {
        this.isAddingSubCollection = false;
        // Only show success if response indicates success
        if (response && (response.success !== false)) {
          this.notificationService.showSuccess('Sub-collection created successfully!');
          this.showAddSubModal = false;
          this.newSubName = '';
          this.subCollectionNameError = '';
          this.selectedCollectionForSub = null;
          this.loadCollectionsFromAPI(); // Reload the list
        } else {
          this.notificationService.showError('Failed to create sub-collection');
          this.showAddSubModal = false;
          this.newSubName = '';
          this.subCollectionNameError = '';
          this.selectedCollectionForSub = null;
        }
      },
      error: (error: any) => {
        this.isAddingSubCollection = false;
        console.trace('Error creating sub-collection:', error);
        this.notificationService.showError('Error creating sub-collection');
        this.showAddSubModal = false;
        this.newSubName = '';
        this.subCollectionNameError = '';
        this.selectedCollectionForSub = null;
      }
    });
  }

  cancelAddSubCollection() {
    this.showAddSubModal = false;
    this.newSubName = '';
    this.subCollectionNameError = '';
    this.selectedCollectionForSub = null;
  }

  editSubCollection(col: any, sub: any) {
    console.info('Edit sub-collection', sub, 'from', col);
    this.selectedCollectionForEditSub = col;
    this.selectedSubForEdit = sub;
    this.editSubName = sub.name;
    this.editSubNameError = '';
    this.showEditSubModal = true;
  }

  checkEditSubCollectionDuplicate() {
    const name = (this.editSubName || '').trim();
    if (!name || !this.selectedSubForEdit || !this.selectedCollectionForEditSub) {
      this.editSubNameError = '';
      return;
    }
    
    const isDuplicate = this.selectedCollectionForEditSub.subCollections?.some(
      (s: any) => s.id !== this.selectedSubForEdit.id && s.name.toLowerCase() === name.toLowerCase()
    ) ?? false;
    if (isDuplicate) {
      this.editSubNameError = this.SUB_COLLECTION_EXISTS_ERROR;
    } else {
      this.editSubNameError = '';
    }
  }

  submitEditSubCollection() {
    const name = (this.editSubName || '').trim();
    if (!name || !this.selectedSubForEdit || !this.selectedCollectionForEditSub) {
      return;
    }
    
    // Check for duplicate sub-collection name within parent (excluding current)
    const isDuplicate = this.selectedCollectionForEditSub.subCollections?.some(
      (s: any) => s.id !== this.selectedSubForEdit.id && s.name.toLowerCase() === name.toLowerCase()
    ) ?? false;
    if (isDuplicate) {
      this.editSubNameError = this.SUB_COLLECTION_EXISTS_ERROR;
      return;
    }
    
    // Call API to update sub-collection
    this.isEditingSubCollection = true;
    const request = {
      // collectionId: this.selectedCollectionForEditSub.id,
      subCollectionId: this.selectedSubForEdit.id,
      subCollectionName: name,
      updatedBy: this.userName
    };

    this.manageCollectionsService.updateSubCollection(this.workDayId, request).subscribe({
      next: (response: any) => {
        this.isEditingSubCollection = false;
        if (response && (response.success !== false)) {
          this.notificationService.showSuccess('Sub-collection updated successfully!');
          this.selectedSubForEdit.name = name;
          this.selectedSubForEdit.updated = new Date().toISOString();
          this.cancelEditSubCollection();
          this.loadCollectionsFromAPI(); // Reload the list
        } else {
          this.isEditingSubCollection = false;
          this.notificationService.showError('Failed to update sub-collection');
        }
      },
      error: (_error: any) => {
        this.isEditingSubCollection = false;
        this.notificationService.showError('Failed to update sub-collection');
      }
    });
  }

  cancelEditSubCollection() {
    this.showEditSubModal = false;
    this.editSubName = '';
    this.editSubNameError = '';
    this.selectedSubForEdit = null;
    this.selectedCollectionForEditSub = null;
  }

  deleteSubCollection(col: any, sub: any) {
    this.deleteConfirmTitle = sub.name;
    this.deleteType = 'sub-collection';
    this.deleteConfirmCallback = () => {
      console.info('Delete sub-collection', sub, 'from', col);
      
      // Call API to delete sub-collection
      this.isDeletingCollection = true;
      this.manageCollectionsService.deleteSubCollection(
        col.id.toString(),
        sub.id.toString(), 
        this.workDayId, 
        this.userName
      ).subscribe({
        next: (response: any) => {
          this.isDeletingCollection = false;
          // Only show success if response indicates success
          if (response && (response.success !== false)) {
            this.notificationService.showSuccess('Sub-collection deleted successfully!');
            
            // Remove from active sub-collections
            col.subCollections = (col.subCollections || []).filter((s: any) => s.id !== sub.id);
          
          // Add to deleted sub-collections
          if (!col.deletedSubCollections) {
            col.deletedSubCollections = [];
          }
          col.deletedSubCollections.unshift({
            id: sub.id,
            name: sub.name,
            deletedAt: sub.updatedAt || sub.updated
          });
          
          // Update counts
          col.activeCount = col.subCollections.length;
          col.deletedCount = col.deletedSubCollections.length;
          
          // Expand the collection to show deleted sub-collections
          col.expanded = true;
          
          // Reload data to get latest updated_at timestamp from API
          this.loadCollectionsFromAPI();
          } else {
            this.notificationService.showError('Failed to delete sub-collection');
          }
        },
        error: (error: any) => {
          this.isDeletingCollection = false;
          console.trace('Error deleting sub-collection:', error);
          this.notificationService.showError('Error deleting sub-collection');
        }
      });
    };
    this.showDeleteConfirmModal = true;
  }

  restoreSubCollection(col: any, sub: any) {
    console.info('Restore sub-collection', sub, 'to', col);
    
    // Call API to restore sub-collection
    this.isDeletingCollection = true;
    this.manageCollectionsService.deleteSubCollection(
      col.id.toString(),
      sub.id.toString(), 
      this.workDayId, 
      this.userName,
      false // isDeleted: false to restore
    ).subscribe({
      next: (response: any) => {
        this.isDeletingCollection = false;
        if (response && (response.success !== false)) {
          this.notificationService.showSuccess('Sub-collection restored successfully!');
          // Reload data to reflect the restoration
          this.loadCollectionsFromAPI();
        } else {
          this.notificationService.showError('Failed to restore sub-collection');
        }
      },
      error: (error: any) => {
        this.isDeletingCollection = false;
        console.trace('Error restoring sub-collection:', error);
        this.notificationService.showError('Error restoring sub-collection');
      }
    });
  }

  filteredCollections() {
    const result = this.collections;
    
    // Apply search term
    const term = this.searchText.trim().toLowerCase();
    if (!term) {
      return result;
    }
    return result.filter(c => c.name.toLowerCase().includes(term));
  }

  filteredSubCollections(col: any) {
    const term = this.searchText.trim().toLowerCase();
    const list = (col?.subCollections || []);
    if (!term) {
      return list;
    }
    return list.filter((s: any) => (s?.name || '').toLowerCase().includes(term));
  }

  filteredDeletedSubCollections(col: any) {
    const term = this.searchText.trim().toLowerCase();
    const list = (col?.deletedSubCollections || []);
    if (!term) {
      return list;
    }
    return list.filter((s: any) => (s?.name || '').toLowerCase().includes(term));
  }

  filteredDeletedCollections() {
    const result = this.deletedCollections;
    
    // Apply search term
    const term = this.searchText.trim().toLowerCase();
    if (!term) {
      return result;
    }
    return result.filter(c => c.name.toLowerCase().includes(term));
  }

  restoreCollection(item: { id: number; name: string; updated: string; deletedAt: string }) {
    console.info('Restore collection', item);
    
    // Call API to restore collection
    this.isDeletingCollection = true;
    this.manageCollectionsService.deleteCollection(
       
      item.id.toString(), 
      this.userName,
      false // isDeleted: false to restore
    ).subscribe({
      next: (response: any) => {
        this.isDeletingCollection = false;
        if (response && (response.success !== false)) {
          this.notificationService.showSuccess('Collection restored successfully!');
          // Reload data to reflect the restoration
          this.loadCollectionsFromAPI();
        } else {
          this.notificationService.showError('Failed to restore collection');
        }
      },
      error: (error: any) => {
        this.isDeletingCollection = false;
        console.trace('Error restoring collection:', error);
        this.notificationService.showError('Error restoring collection');
      }
    });
  }

  // Dynamic count calculation methods
  getActiveCollectionsCount(): number {
    return this.collections.length;
  }

  getActiveSubCollectionsCount(): number {
    return this.collections.reduce((total, col) => {
      return total + (col.subCollections?.length || 0);
    }, 0);
  }

  getDeletedSubCollectionsCount(): number {
    return this.collections.reduce((total, col) => {
      return total + (col.deletedSubCollections?.length || 0);
    }, 0);
  }

  getDeletedCollectionsCount(): number {
    return this.deletedCollections.length;
  }

  loadCollectionsFromAPI() {
    if (!this.workDayId) {
      this.notificationService.showError('Unable to load collections: Missing WorkdayID');
      return;
    }

    this.manageCollectionsService.fetchCollections(this.workDayId).subscribe({
      next: (response: any) => {
        // Handle response structure: { statusCode, valid, message, payload: { response: [...] } }
        const apiCollections = response?.payload?.response || response?.payload || response || [];
        
        // Separate active and deleted collections
        const allCollections = apiCollections.map((col: any) => {
          // Split sub-collections into active and deleted
          const activeSubCollections = (col.sub_collections || []).filter((sub: any) => !sub.is_deleted);
          const deletedSubCollections = (col.sub_collections || []).filter((sub: any) => sub.is_deleted);
          
          return {
            id: col.collection_id,
            name: col.collection_name,
            isFavorite: col.is_favorite || false,
            updated: col.created_at ,
            createdAt: col.created_at,
            updatedAt: col.updated_at,
            activeCount: activeSubCollections.length,
            deletedCount: deletedSubCollections.length,
            expanded: false,
            isDeleted: col.is_deleted || false,
            subCollections: activeSubCollections.map((sub: any) => ({
              id: sub.sub_collection_id,
              name: sub.sub_collection_name,
              updated: sub.created_at ,
              updatedAt: sub.updated_at,
              isDeleted: sub.is_deleted || false
            })),
            deletedSubCollections: deletedSubCollections.map((sub: any) => ({
              id: sub.sub_collection_id,
              name: sub.sub_collection_name,
              updated: sub.created_at,
              deletedAt: sub.updated_at ,
              isDeleted: true
            }))
          };
        });

        // Separate active and deleted collections
        this.collections = allCollections.filter((col: any) => !col.isDeleted).sort((a: any, b: any) => {
          // Sort by created_at in descending order (newest first)
          const aTime = new Date(a.createdAt).getTime();
          const bTime = new Date(b.createdAt).getTime();
          return bTime - aTime;
        });
        this.deletedCollections = allCollections.filter((col: any) => col.isDeleted).sort((a: any, b: any) => {
          // Sort by updated_at in descending order (newest first)
          const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          return bTime - aTime;
        }).map((col: any) => ({
          id: col.id,
          name: col.name,
          updated: col.updated,
          deletedAt: col.updatedAt ,
          expanded: col.expanded,
          deletedSubCollections: col.deletedSubCollections
        }));
      },
      error: (error: any) => {
        console.trace('Error loading collections:', error);
        this.notificationService.showError('Error loading collections from server');
      }
    });
  }

}
